/*
 * @Description: 
 * @Author: zhanjunwei
 * @Date: 2025-01-09 14:31:32
 * @LastEditors: zhanjunwei
 * @LastEditTime: 2025-01-18 16:33:29
 */
'use client';

import React, {
  Fragment,
  ComponentType,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import { usePage, getMobile } from '@ssv-website/hooks';
import { ArrowPage } from './ArrowPage';
import useScrollPage from './useScrollPage';
import { DotPage } from './DotPage';
import { useMedia } from '@ssv-website/hooks';
import { animated } from '@react-spring/web';
import * as R from 'ramda';
import { ScrollPage } from './ScrollPage';
import './index.css';

interface IPageDeviceList {
  data: Array<any>;
  className?: string;
  columns?: number;
  renderItem: (item: any, index: number) => React.ReactNode;
  disablePage?: boolean;
  ContainerComponent?: ComponentType<{ children: React.ReactNode }>;
  gap?: number;
}
export { DotPage };

export interface IPageList extends IPageDeviceList {
  limit?: number;
  disablePage?: boolean;
}

const PcPageList = ({
  data = [],
  className = '',
  renderItem,
  columns = 1,
  disablePage = false,
  ContainerComponent = Fragment,
}: IPageDeviceList) => {
  const {
    current,
    pageSize,
    totalPage,
    enableNext,
    enablePrev,
    onPrev,
    onNext,
  } = usePage({ page: 1, pageSize: columns, total: data.length, pageFrom: 1 });
  const renderData =
    pageSize > 0
      ? data.slice((current - 1) * pageSize, current * pageSize)
      : data;
  return (
    <div>
      <ContainerComponent>
        <div className={`${className}`}>
          {renderData.map((item, index) => (
            <Fragment key={index}>{renderItem?.(item, index)}</Fragment>
          ))}
        </div>
      </ContainerComponent>
      {!disablePage && totalPage > 1 && (
        <ArrowPage
          className="mt-[30px]"
          current={current}
          total={totalPage}
          enableLeft={enablePrev}
          enableRight={enableNext}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
    </div>
  );
};

export const MobilePageList = ({
  data = [],
  className = '',
  renderItem,
  columns = 1,
  disablePage = false,
  gap = 20,
}: IPageDeviceList) => {
  const renderData = useMemo(
    () => (columns > 0 ? R.splitEvery(columns, data) : [data]),
    [data, columns]
  );
  const { ref, current, bind, style } = useScrollPage({
    total: renderData.length,
    gap,
  });
  const rest = disablePage ? {} : { ...bind() };
  // console.log(renderData, 'renderData');

  return (
    <div>
      <div ref={ref} {...rest} className="mobile:overflow-hidden touch-pan-y">
        <animated.div className="flex gap-5" style={style}>
          {renderData.map((items, index) => (
            <div
              key={index}
              className={`w-full flex-shrink-0 ${className} ${
                !disablePage && renderData.length > 1 ? 'mb-5' : ''
              } transition-transform duration-300 ease-in-out`}
            >
              {items.map((item, i) => (
                <Fragment key={i}>{renderItem?.(item, i)}</Fragment>
              ))}
            </div>
          ))}
        </animated.div>
      </div>
      {!disablePage && renderData.length > 1 && (
        <DotPage total={renderData.length} current={current} />
      )}
    </div>
  );
};

export const ScrollPageList = ({
  data = [],
  className = '',
  renderItem,
  columns = 1,
  disablePage = false,
  gap = 20,
}: IPageDeviceList) => {
  const renderData = useMemo(
    () => R.splitEvery(columns, data),
    [data, columns]
  );
  const pageRef = useRef<any>();
  const isMobile = useMedia('mobile');
  const { ref, current, bind, style, onScrollChange, onChange } = useScrollPage(
    {
      total: renderData.length,
      gap,
      scrollable: !isMobile,
      scrollCallback: (
        current: number,
        ranges: [range1: number, range2: number],
        active: boolean
      ) => {
        if (pageRef.current) {
          pageRef.current.scrollTo(current, ranges, active);
        }
      },
    }
  );
  const rest = disablePage ? {} : { ...bind() };
  return (
    <div>
      <div
        ref={ref}
        {...rest}
        className={`${
          isMobile ? 'overflow-hidden' : 'overflow-x-scroll'
        } hide-scrollbar touch-pan-y`}
      >
        <animated.div className="flex" style={{ ...style, gap }}>
          {renderData.map((items, index) => (
            <div
              key={index}
              className={`w-full flex-shrink-0 ${className} ${
                !disablePage && renderData.length > 1 ? 'mb-5' : ''
              } transition-transform duration-300 ease-in-out`}
            >
              {items.map((item, i) => (
                <Fragment key={i}>{renderItem?.(item, i)}</Fragment>
              ))}
            </div>
          ))}
        </animated.div>
      </div>
      {!disablePage &&
        renderData.length > 1 &&
        (isMobile ? (
          <DotPage total={renderData.length} current={current} />
        ) : (
          <ScrollPage
            ref={pageRef}
            total={renderData.length}
            page={current}
            onScrollChange={onScrollChange}
            onChange={(page) => onChange(page, false)}
          />
        ))}
    </div>
  );
};

export const PageList = ({ limit = 0, ...props }: IPageList) => {
  const isMobile = useMedia('mobile');

  const newData =
    limit > 0 && props.data.length > limit
      ? props.data.slice(0, limit)
      : props.data;
  return isMobile ? (
    <MobilePageList {...props} data={newData} />
  ) : (
    <PcPageList {...props} data={newData} />
  );
};

export { ScrollPage };
